import { useState, useCallback, useEffect } from "react";
import { message } from "antd";
import apiClient from "utils/apiClient";
import { OVIZIA_SERVICE_BASE_URL } from "constants";

const BASE_URL = `${OVIZIA_SERVICE_BASE_URL}/api/v1/admin/newsletter-templates`;
const DEFAULT_PAGE = 0;
const DEFAULT_SIZE = 20;

export default function useNewsletterTemplatesAdmin() {
  const authToken = sessionStorage.getItem("authToken");

  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(false);
  const [initialLoad, setInitialLoad] = useState(true);
  const [error, setError] = useState(null);
  const [totalPages, setTotalPages] = useState(1);
  const [totalElements, setTotalElements] = useState(0);

  const handleAxiosErrors = (err, action = "operation") => {
    let msg = "An unexpected error occurred.";

    if (err.response) {
      const status = err.response.status;
      if (status >= 500) {
        msg = "Server error. Please try again later.";
      } else {
        msg =
          err.response.data?.message ||
          `Failed to ${action}: ${err.response.statusText}`;
      }
    } else if (err.request) {
      msg = "No response from server. Check your connection.";
    } else {
      msg = err.message || msg;
    }

    message.error(msg);
    console.error(`${action} error:`, err);
    setError(msg);
  };

  const formatTemplate = (data) => ({
    id: data.id,
    name: data.name,
    category: data.category,
    thumbnailUrl: data.thumbnailUrl,
    htmlBody: data.htmlBody,
    premium: data.premium,
    source: data.source,
    createdAt: data.createdAt,
  });

  const handlePaginatedResponse = (res, page = 0) => {
    const { content = [], totalPages = 1, totalElements = 0 } = res.data;
    setTemplates((prev) =>
      page === 0
        ? content.map(formatTemplate)
        : [...prev, ...content.map(formatTemplate)],
    );
    setTotalPages(totalPages);
    setTotalElements(totalElements);
  };

  const fetchAll = useCallback(
    async (page = DEFAULT_PAGE, size = DEFAULT_SIZE) => {
      if (!authToken) return;
      setLoading(true);
      setError(null);
      try {
        const res = await apiClient.get(BASE_URL, { params: { page, size } });
        handlePaginatedResponse(res, page);
      } catch (err) {
        handleAxiosErrors(err, "fetch newsletter templates");
      } finally {
        setLoading(false);
        setInitialLoad(false);
      }
    },
    [authToken],
  );

  const getTemplateById = useCallback(
    async (id) => {
      if (!authToken || !id) return;
      try {
        const res = await apiClient.get(`${BASE_URL}/${id}`);
        return formatTemplate(res.data);
      } catch (err) {
        handleAxiosErrors(err, "fetch newsletter template");
        throw err;
      }
    },
    [authToken],
  );

  const createTemplate = useCallback(
    async (payload) => {
      if (!authToken) return;
      setLoading(true);
      setError(null);
      try {
        const res = await apiClient.post(BASE_URL, payload);
        const created = formatTemplate(res.data);
        setTemplates((prev) => [created, ...prev]);
        setTotalElements((prev) => prev + 1);
        message.success("Newsletter template created");
        return created;
      } catch (err) {
        handleAxiosErrors(err, "create newsletter template");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authToken],
  );

  const updateTemplate = useCallback(
    async (id, payload) => {
      if (!authToken || !id) return;
      setLoading(true);
      setError(null);
      try {
        const res = await apiClient.put(`${BASE_URL}/${id}`, payload);
        const updated = formatTemplate(res.data);
        setTemplates((prev) => prev.map((t) => (t.id === id ? updated : t)));
        message.success("Newsletter template updated");
        return updated;
      } catch (err) {
        handleAxiosErrors(err, "update newsletter template");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authToken],
  );

  const deleteTemplate = useCallback(
    async (id) => {
      if (!authToken || !id) return;
      setLoading(true);
      setError(null);
      try {
        await apiClient.delete(`${BASE_URL}/${id}`);
        setTemplates((prev) => prev.filter((t) => t.id !== id));
        setTotalElements((prev) => Math.max(0, prev - 1));
        message.success("Newsletter template deleted");
      } catch (err) {
        handleAxiosErrors(err, "delete newsletter template");
        throw err;
      } finally {
        setLoading(false);
      }
    },
    [authToken],
  );

  useEffect(() => {
    if (authToken) fetchAll();
  }, [authToken, fetchAll]);

  return {
    templates,
    loading,
    error,
    initialLoad,
    totalPages,
    totalElements,
    fetchAll,
    getTemplateById,
    createTemplate,
    updateTemplate,
    deleteTemplate,
  };
}
